import { useCallback, useMemo, type FC } from 'react';

import { useGetOwnChats } from '@/features/api/hooks/grapevine-chat/useGetOwnChats';
import { useStartChatOnGroup } from '@/features/api/hooks/grapevine-chat-events/useStartChatOnGroup';
import { useGetAllGroupMembers } from '@/features/api/hooks/grapevine-group/useGetAllGroupMembers';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { NewChatOnEntity } from '@/features/chats/components/NewChatOnEntity';
import { findChatBetweenPersons } from '@/features/chats/utils/chat';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';

type Props = AppNativeStackScreenProps<typeof AppRoute.NewChatOnGroup>;

export const ChatOnGroupScreen: FC<Props> = ({ route }) => {
  const { groupId, memberId } = route.params;

  const { userId: ownUserId } = useAppState();
  const { data: members, isLoading: isMembersLoading } =
    useGetAllGroupMembers(groupId);
  const { data: chats, isLoading: isChatLoading } = useGetOwnChats();
  const { mutateAsync: startChatOnGroup } = useStartChatOnGroup(
    groupId,
    memberId
  );

  const sendMessage = useCallback(
    async (message: string, temporaryMediaItemIds: string[]) => {
      const response = await startChatOnGroup({
        message,
        temporaryMediaItemIds
      });
      return response.chat.id;
    },
    [startChatOnGroup]
  );

  const entityPerson = useMemo(
    () => members?.find((member) => member.id === memberId),
    [members, memberId]
  );

  const existingChat = useMemo(
    () =>
      chats &&
      entityPerson &&
      findChatBetweenPersons(chats, ownUserId, entityPerson.id),
    [chats, entityPerson, ownUserId]
  );

  return (
    <NewChatOnEntity
      sendMessage={sendMessage}
      entityPerson={entityPerson}
      existingChat={existingChat}
      loading={isMembersLoading || isChatLoading}
    />
  );
};
